import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { formatPhoneNumber } from '../../../shared/utils/format-phone-number.util';
import { ADMIN_DELIVERY_POINT_APP_PATHS } from '../../constants/delivery-points-app-paths.constants';
import { DeliveryPoint, DeliveryPointSortColumn, DeliveryPointSortDirection } from '../../types/delivery-point.type';
import DeliveryPointTypeChip from '../Chips/DeliveryPointTypeChip.component';

interface DeliveryPointsTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
  sortColumn?: DeliveryPointSortColumn;
}

interface DeliveryPointsTableProps {
  deliveryPoints: DeliveryPoint[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: DeliveryPointSortColumn;
  sortDirection: DeliveryPointSortDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: DeliveryPointSortColumn) => void;
}

const EMPTY_CELL = '-';

const TABLE_HEADERS: DeliveryPointsTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 50, align: 'center' },
  { key: 'name', label: 'Nom', minWidth: 220, align: 'left', sortColumn: 'name' },
  { key: 'type', label: 'Type', minWidth: 140, align: 'left', sortColumn: 'type' },
  { key: 'segment', label: 'Segment', minWidth: 160, align: 'left' },
  { key: 'city', label: 'Ville', minWidth: 120, align: 'left' },
  { key: 'zipCode', label: 'Code postal', minWidth: 100, align: 'left' },
  { key: 'contact', label: 'Contact', minWidth: 200, align: 'left' },
  { key: 'status', label: 'Statut', minWidth: 100, align: 'center' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

function DeliveryPointsTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: DeliveryPointsTableHeader;
  sortBy: DeliveryPointSortColumn;
  sortDirection: DeliveryPointSortDirection;
  onSortChange: (column: DeliveryPointSortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      aria-label={header.ariaLabel}
      align={header.align}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'asc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

function DeliveryPointRow({ deliveryPoint }: { deliveryPoint: DeliveryPoint }) {
  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="center">
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_DELIVERY_POINT_APP_PATHS.DELIVERY_POINT_DETAILS_PATH(deliveryPoint.id)}
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell align="left">{deliveryPoint.name}</TableCell>
      <TableCell align="left">
        <DeliveryPointTypeChip type={deliveryPoint.type} />
      </TableCell>
      <TableCell align="left">{deliveryPoint.segment?.name ?? EMPTY_CELL}</TableCell>
      <TableCell align="left">{deliveryPoint.address.city || EMPTY_CELL}</TableCell>
      <TableCell align="left">{deliveryPoint.address.zipCode || EMPTY_CELL}</TableCell>
      <TableCell align="left">
        <span style={{ display: 'block' }}>{formatPhoneNumber(deliveryPoint.phone) || EMPTY_CELL}</span>
        <span>{deliveryPoint.email || EMPTY_CELL}</span>
      </TableCell>
      <TableCell align="center">
        <StatusChip isActive={deliveryPoint.isActive} archived={Boolean(deliveryPoint.archivedAt)} />
      </TableCell>
    </TableRow>
  );
}

export function DeliveryPointsTable({
  deliveryPoints,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: DeliveryPointsTableProps) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <DeliveryPointsTableHeaderCell
                  key={header.key}
                  header={header}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {deliveryPoints.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucun point de livraison trouvé
                  </TableCell>
                </TableRow>
              )}
              {deliveryPoints.map((deliveryPoint) => (
                <DeliveryPointRow key={deliveryPoint.id} deliveryPoint={deliveryPoint} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
